/**
 * What needs an admin — FR-ADMIN-11, AC-44.
 *
 * The club's failures are quiet ones. A result that synced four hours after
 * the session was committed sat waiting for a ruling and was found by accident.
 * The nightly backup failed for a week and reported success throughout. Neither
 * was discovered by looking in the right place, because there was no right
 * place to look.
 *
 * This is that place, and its most important state is the boring one: **nothing
 * needs you**. A panel that only ever appears when something is wrong teaches
 * nobody where to check; one that says "all clear" is worth opening.
 *
 * Every entry links to the screen that resolves it. Reporting a problem without
 * offering the way to fix it is how the post-commit arrival went unresolved for
 * days — the member could see the message and had nowhere to go.
 */

import { useEffect, useState } from "react";
import { api, type AttentionItem, type AttentionReport } from "../api.js";

export function AttentionPanel({
  onOpenSession,
}: {
  onOpenSession?: (sessionId: string) => void;
}): React.JSX.Element {
  const [report, setReport] = useState<AttentionReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .attention()
      .then(setReport)
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : "Could not check."),
      );
  }, []);

  if (error) {
    // NFR-23a's rule, applied to the panel itself: a check that could not run
    // says so rather than showing an encouraging empty list.
    return (
      <section className="block">
        <h3 className="card-head">Needs attention</h3>
        <p className="error">{error}</p>
        <p className="muted small">
          This check did not run. That is not the same as nothing being wrong.
        </p>
      </section>
    );
  }

  if (!report) return <p className="muted small">Checking…</p>;

  return (
    <section className="block">
      <h3 className="card-head">
        Needs attention
        {report.count > 0 ? <span className="badge-warn count">{report.count}</span> : null}
      </h3>

      {report.items.length === 0 ? (
        <p className="allclear">Nothing needs you.</p>
      ) : (
        <ul className="attention">
          {report.items.map((item, i) => (
            <li key={`${item.kind}-${String(i)}`} className={item.severity}>
              <strong>{item.title}</strong>
              <span className="muted small">{item.detail}</span>
              {item.since ? (
                <span className="muted small">Since {formatWhen(item.since)}</span>
              ) : null}
              {item.sessionId && onOpenSession ? (
                <button className="mini" onClick={() => onOpenSession(item.sessionId!)}>
                  {resolveLabel(item)}
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      <BackupLine report={report} />
    </section>
  );
}

/**
 * The backup gets its own line whatever its state, unlike the other checks.
 *
 * The rest of the panel is exceptions — an empty list means all is well. A
 * backup is different: "no news" is exactly what it looked like for the week it
 * was broken, so this says the date and the row count out loud even when it is
 * healthy. Seeing yesterday's date and a plausible number is the whole check a
 * human can do at a glance.
 */
function BackupLine({ report }: { report: AttentionReport }): React.JSX.Element {
  const b = report.backup;
  const tone = b.state === "ok" ? "ok" : b.state === "unknown" ? "warning" : "problem";

  // When the backup is not ok it already has its own entry in the list above,
  // carrying the explanation. Repeating it here said the same sentence twice
  // on a screen whose whole job is to be quickly scannable.
  const explained = b.state !== "ok";

  return (
    <p className={`backupline ${tone}`}>
      <strong>Backup</strong>{" "}
      {b.lastVerified ? (
        <>
          last verified {formatWhen(b.lastVerified)}
          {b.rows === null ? null : ` · ${String(b.rows)} rows`}
          {b.offsite === "ok" ? " · copied offsite" : null}
        </>
      ) : (
        "never verified"
      )}
      {explained ? null : <span className="muted small"> · {b.message}</span>}
    </p>
  );
}

function resolveLabel(item: AttentionItem): string {
  switch (item.kind) {
    case "duplicate":
      return "Resolve duplicates";
    case "conflict":
      return "Resolve conflicts";
    case "post_commit_arrival":
      return "Review the late result";
    case "uncommitted_session":
      return "Finish the session";
    case "same_day_session":
      return "Open the sessions";
    default:
      return "Open";
  }
}

/**
 * Parsed with the timezone the server sent, then rendered in the reader's own.
 * "Since 23:45" on a phone is the number a human compares against now.
 */
function formatWhen(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  const days = Math.floor((Date.now() - at.getTime()) / 86_400_000);
  if (days >= 1) return `${String(days)} day${days === 1 ? "" : "s"} ago`;
  return at.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}
